import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, Link, useNavigate, useLocation } from 'react-router-dom';
import { APIProvider } from '@vis.gl/react-google-maps';
import { CompanyProvider } from './context/CompanyContext';
import './App.css';
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import AdminPanel from './pages/AdminPanel';
import CalendarView from './pages/CalendarView';
import Customers from './pages/Customers';
import Tasks from './pages/Tasks';
import BusinessCards from './pages/BusinessCards';
import Leads from './pages/Leads';
import SiteSurveySimulation from './pages/SiteSurveySimulation';
import AGVDesigner from './pages/AGVDesigner';

import DatabaseAdmin from './pages/DatabaseAdmin';
import SystemArchitecture from './pages/SystemArchitecture';
import ImpersonationBanner from './components/ImpersonationBanner';
import Sidebar from './components/Sidebar';
import Topbar from './components/Topbar';
import MobileNav from './components/MobileNav';

// Helper to send logs
const logUserActivity = (email, action_type, details = '') => {
  if (!email) return;
  fetch('/api/log_activity', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, action_type, details })
  }).catch(err => console.error('Failed to log activity:', err));
};

// Track page views
const RouteTracker = () => {
  const location = useLocation();
  
  useEffect(() => {
    const userStr = localStorage.getItem('user');
    if (userStr) {
      try {
        const user = JSON.parse(userStr);
        logUserActivity(user.google_email, 'PAGE_VIEW', location.pathname + location.search);
      } catch (e) {}
    }
  }, [location]);

  return null;
};

// Protected Route Component
const ProtectedRoute = ({ children, requireAdmin = false }) => {
  const userStr = localStorage.getItem('user');
  if (!userStr) {
    return <Navigate to="/login" replace />;
  }
  const user = JSON.parse(userStr);
  if (requireAdmin && user.role !== 'admin' && user.role !== 'super_admin') {
    return <Navigate to="/" replace />; // Redirect non-admins away from admin panel
  }
  return children;
};

// Sys Admin Route Component
const SysAdminRoute = ({ children }) => {
  const userStr = localStorage.getItem('user');
  if (!userStr) {
    return <Navigate to="/login" replace />;
  }
  const user = JSON.parse(userStr);
  if (user.google_email !== 'techin@tmhwst.toyota-industries.com') {
    return <Navigate to="/" replace />;
  }
  return children;
};

// Layout Component
const Layout = ({ children }) => {
  const navigate = useNavigate();

  const userStr = localStorage.getItem('user');
  const user = userStr ? JSON.parse(userStr) : null;

  const handleLogout = (isAutoLogout = false) => {
    if (!isAutoLogout && user) {
      logUserActivity(user.google_email, 'LOGOUT', 'User manually logged out');
    }
    localStorage.removeItem('user');
    localStorage.removeItem('impersonator_admin');
    localStorage.removeItem('lastActivity');
    navigate('/login');
  };

  useEffect(() => {
    if (!userStr) return; // Only track if logged in

    const TIMEOUT_MS = 6 * 60 * 60 * 1000; // 6 hours
    let timeoutId;
    let throttleTimeout;

    const checkAndLogout = () => {
      const lastActivity = localStorage.getItem('lastActivity');
      if (lastActivity && Date.now() - parseInt(lastActivity) > TIMEOUT_MS) {
        if (userStr) {
          try {
            const user = JSON.parse(userStr);
            logUserActivity(user.google_email, 'AUTO_LOGOUT', 'Session expired after 6 hours');
          } catch (e) {}
        }
        handleLogout(true);
        alert('หมดเวลาการใช้งาน (ไม่มีความเคลื่อนไหวเกิน 6 ชั่วโมง) กรุณาเข้าสู่ระบบใหม่');
        return true; // Was logged out
      }
      return false;
    };

    const resetTimer = () => {
      if (checkAndLogout()) return;
      
      // Update activity timestamp in local storage
      localStorage.setItem('lastActivity', Date.now().toString());
      
      if (timeoutId) clearTimeout(timeoutId);
      timeoutId = setTimeout(checkAndLogout, TIMEOUT_MS);
    };

    const handleActivity = () => {
      // Throttle resetTimer to run at most once per second to save performance
      if (!throttleTimeout) {
        throttleTimeout = setTimeout(() => {
          resetTimer();
          throttleTimeout = null;
        }, 1000);
      }
    };

    // Check immediately on app load
    if (!checkAndLogout()) {
      resetTimer();
      const events = ['mousedown', 'mousemove', 'keydown', 'scroll', 'touchstart'];
      events.forEach(e => document.addEventListener(e, handleActivity, { passive: true }));
    }

    return () => {
      if (timeoutId) clearTimeout(timeoutId);
      if (throttleTimeout) clearTimeout(throttleTimeout);
      const events = ['mousedown', 'mousemove', 'keydown', 'scroll', 'touchstart'];
      events.forEach(e => document.removeEventListener(e, handleActivity));
    };
  }, [userStr, navigate]);

  return (
    <div className="app-container">
      <Sidebar user={user} />
      <div className="main-wrapper">
        <ImpersonationBanner />
        <Topbar user={user} onLogout={handleLogout} />
        <main className="main-content">
          {children}
        </main>
      </div>
      <MobileNav user={user} />
    </div>
  );
};

const API_KEY = import.meta.env.VITE_GOOGLE_MAPS_API_KEY || "AIzaSyALpDWAlpYMJc5RtcLG31oOvXYVRmwthXw";
const LIBRARIES = ['places', 'routes', 'geocoding'];

function App() {
  return (
    <CompanyProvider>
      <APIProvider apiKey={API_KEY} version="3.60" libraries={LIBRARIES}>
        <Router>
          <RouteTracker />
          <Routes>
          <Route path="/login" element={<Login />} />
          
          {/* Protected Routes */}
          <Route path="/" element={<ProtectedRoute><Layout><Dashboard /></Layout></ProtectedRoute>} />
          <Route path="/customers" element={<ProtectedRoute><Layout><Customers /></Layout></ProtectedRoute>} />
          <Route path="/tasks" element={<ProtectedRoute><Layout><Tasks /></Layout></ProtectedRoute>} />
          <Route path="/visits" element={<ProtectedRoute><Layout><Tasks /></Layout></ProtectedRoute>} />
          <Route path="/calendar" element={<ProtectedRoute><Layout><CalendarView /></Layout></ProtectedRoute>} />
          <Route path="/business-cards" element={<ProtectedRoute><Layout><BusinessCards /></Layout></ProtectedRoute>} />
          <Route path="/admin" element={<ProtectedRoute requireAdmin={true}><Layout><AdminPanel /></Layout></ProtectedRoute>} />
          <Route path="/leads" element={<ProtectedRoute><Layout><Leads /></Layout></ProtectedRoute>} />
          <Route path="/simulation" element={<ProtectedRoute><Layout><SiteSurveySimulation /></Layout></ProtectedRoute>} />
          <Route path="/agv-designer" element={<ProtectedRoute requireAdmin={true}><Layout><AGVDesigner /></Layout></ProtectedRoute>} />

          <Route path="/db-admin" element={<SysAdminRoute><Layout><DatabaseAdmin /></Layout></SysAdminRoute>} />
          <Route path="/architecture" element={<ProtectedRoute requireAdmin={true}><Layout><SystemArchitecture /></Layout></ProtectedRoute>} />
          
          {/* Fallback */}
          <Route path="*" element={<Navigate to="/" />} />
        </Routes>
      </Router>
      </APIProvider>
    </CompanyProvider>
  );
}

export default App;
